// PersonalZugangPage.jsx — Einladung annehmen / Passwort neu setzen (/personal-zugang/:token, 18.10.2026)
//
// Der Link kommt vom Veranstalter (Dashboard → Personal → „Einladungslink“)
// oder aus „Passwort vergessen“. Die Person legt hier ihr EIGENES Passwort
// fest (und, falls noch keiner vergeben ist, ihren Benutzernamen) und meldet
// sich danach auf der gemeinsamen Anmeldeseite an.
// Server: routes/personalZugang.js, services/personalKonto.js.
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import './AnmeldenPage.css';
import PasswortRegeln from '../components/passwort/PasswortRegeln';
import { passwortFehler } from '../lib/passwortRegel';

export default function PersonalZugangPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);       // null = lädt
  const [fehler, setFehler] = useState('');
  const [benutzername, setBenutzername] = useState('');
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [laedt, setLaedt] = useState(false);
  const [fertig, setFertig] = useState(null);

  useEffect(() => {
    let ab = false;
    fetch(`/api/personal-zugang/${encodeURIComponent(token || '')}`)
      .then(async (r) => {
        const d = await r.json().catch(() => ({}));
        if (!r.ok || !d.success) throw new Error(d.error || `Fehler ${r.status}`);
        if (!ab) setInfo(d);
      })
      .catch((e) => { if (!ab) { setInfo(false); setFehler(e.message === 'Failed to fetch' ? 'Keine Verbindung zum Server.' : e.message); } });
    return () => { ab = true; };
  }, [token]);

  const absenden = async (e) => {
    e.preventDefault();
    setFehler('');
    // Eine Passwortregel für alle Konten (25.10.2026, lib/passwortRegel.js)
    const pwFehler = passwortFehler(pw1);
    if (pwFehler) { setFehler(`${pwFehler}.`); return; }
    if (pw1 !== pw2) { setFehler('Die beiden Passwörter stimmen nicht überein.'); return; }
    setLaedt(true);
    try {
      const r = await fetch(`/api/personal-zugang/${encodeURIComponent(token)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passwort: pw1, benutzername: info?.benutzername_waehlen ? benutzername.trim().toLowerCase() : undefined }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.success) throw new Error(d.error || `Fehler ${r.status}`);
      setFertig(d.benutzername);
    } catch (err) {
      setFehler(err.message === 'Failed to fetch' ? 'Keine Verbindung zum Server.' : err.message);
    } finally {
      setLaedt(false);
    }
  };

  const passwortNeu = info?.zweck === 'passwort';
  return (
    <div className="anm-seite">
      <div className="anm-karte">
        <div className="anm-kopf">
          <Link to="/" className="anm-logo-link" aria-label="Zur Startseite">
            <img src="/logo.webp" alt="TDA" className="anm-logo" width="64" height="64" />
          </Link>
          <span className="anm-marke">TDA Tournament Center</span>
          <h1>{passwortNeu ? 'Neues Passwort' : 'Einladung annehmen'}</h1>
          {info && (
            <p>
              {info.name}{info.verband ? ` · ${info.verband}` : ''}
              {info.turnier ? <><br />{info.turnier}</> : null}
            </p>
          )}
        </div>

        {info === null && <p className="anm-hinweis">Link wird geprüft…</p>}

        {info === false && (
          <div className="anm-form">
            <div className="anm-fehler" role="alert">{fehler}</div>
            <Link to="/anmelden" className="anm-vergessen">Zur Anmeldung</Link>
          </div>
        )}

        {info && fertig && (
          <div className="anm-form">
            <p className="anm-hinweis">Passwort gespeichert. Du meldest dich ab jetzt mit dem Benutzernamen <strong>{fertig}</strong> und deinem Passwort an.</p>
            <button className="anm-knopf" onClick={() => navigate(`/anmelden?name=${encodeURIComponent(fertig)}`)}>Zur Anmeldung</button>
          </div>
        )}

        {info && !fertig && (
          <form className="anm-form" onSubmit={absenden}>
            <p className="anm-hinweis">
              {passwortNeu
                ? 'Lege ein neues Passwort fest.'
                : 'Du wurdest als Turnierpersonal eingeladen. Lege dein eigenes Passwort fest; danach siehst du nach der Anmeldung, wofür du eingeteilt bist.'}
              {' '}Der Link gilt bis {info.gueltig_bis} und nur einmal.
            </p>
            {info.benutzername_waehlen ? (
              <label className="anm-feld">Benutzername
                <input value={benutzername} onChange={(e) => setBenutzername(e.target.value)} required autoFocus
                  autoComplete="username" autoCapitalize="none" spellCheck={false} placeholder="z. B. mia.muster" minLength={3} maxLength={50}
                  title="3 bis 50 Zeichen: Buchstaben, Ziffern, Punkt, Bindestrich, Unterstrich" />
              </label>
            ) : (
              <p className="anm-grau">Benutzername: <strong>{info.benutzername}</strong></p>
            )}
            <label className="anm-feld">Passwort
              <input type="password" value={pw1} onChange={(e) => setPw1(e.target.value)} required minLength={info.min_passwort}
                autoComplete="new-password" autoFocus={!info.benutzername_waehlen} aria-describedby="pw-regeln" />
              <PasswortRegeln passwort={pw1} id="pw-regeln" />
            </label>
            <label className="anm-feld">Passwort wiederholen
              <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required autoComplete="new-password" />
            </label>
            {fehler && <div className="anm-fehler" role="alert">{fehler}</div>}
            <button className="anm-knopf" disabled={laedt}>{laedt ? 'Wird gespeichert…' : 'Passwort speichern'}</button>
          </form>
        )}
      </div>
      <Link to="/anmelden" className="anm-zurueck">← Zur Anmeldung</Link>
    </div>
  );
}
